// core/stores/communityStore.ts — Zustand store for communities + invites.
//
// Hydrated at module init from the typed seed (deep-clone so test resets
// don't mutate the imported seed constants).
// Services mutate this store; hooks read it reactively.
// Components must use hooks, not this store directly.

import { create } from 'zustand';
import type { Community, CommunityInvite } from '@/core/models/community';
import { seedCommunities, seedCommunityInvites } from '@/core/data/communities';
import { bootSeed } from './bootSeed';

interface CommunityState {
  communities: Community[];
  invites: CommunityInvite[];

  /** Replace the entire communities list (used by _resetForTest). */
  setCommunities: (communities: Community[]) => void;

  /** Replace the entire invites list (hydration). */
  setInvites: (invites: CommunityInvite[]) => void;

  /** Prepend a postId to a community's feed (no-op if id absent). */
  addPostToCommunity: (communityId: string, postId: string) => void;

  /** Prepend a new community record to the list. */
  addCommunity: (community: Community) => void;
}

export const useCommunityStore = create<CommunityState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate imported constants.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  communities: bootSeed(seedCommunities).map((c) => ({
    ...c,
    rules: [...c.rules],
    moderators: c.moderators.map((m) => ({ ...m })),
    events: c.events.map((e) => ({ ...e })),
    postIds: [...c.postIds],
  })),
  invites: bootSeed(seedCommunityInvites).map((inv) => ({ ...inv })),

  setCommunities: (communities) => set({ communities }),

  setInvites: (invites) => set({ invites }),

  addPostToCommunity: (communityId, postId) =>
    set((s) => ({
      communities: s.communities.map((c) =>
        c.id === communityId ? { ...c, postIds: [postId, ...c.postIds] } : c,
      ),
    })),

  addCommunity: (community) =>
    set((s) => ({ communities: [community, ...s.communities] })),
}));
